/**
 * Crown01 — GDPR-friendly cookie consent.
 *
 * Slim bottom banner + "Manage Preferences" modal.
 * Categories:
 *   - Necessary (always on)
 *   - Analytics (GA4)
 *   - Marketing (Meta Pixel, TikTok Pixel)
 *
 * Persists the visitor's choice in localStorage (crown01_cookie_consent).
 * Exposes window.openCookiePreferences() so the footer "Cookie Preferences"
 * link (and any other element) can reopen the modal later.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getConsent, setConsent } from "./analytics";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  // On mount: if no stored choice → show banner; otherwise hydrate prefs.
  useEffect(() => {
    const stored = getConsent();
    if (!stored) {
      setShowBanner(true);
    } else {
      setPrefs({ analytics: !!stored.analytics, marketing: !!stored.marketing });
    }
    // Expose a global so anything else (footer link) can reopen.
    window.openCookiePreferences = () => {
      const s = getConsent();
      setPrefs({ analytics: !!s?.analytics, marketing: !!s?.marketing });
      setShowModal(true);
    };
    return () => { try { delete window.openCookiePreferences; } catch { /* noop */ } };
  }, []);

  const acceptAll = () => {
    setConsent({ analytics: true, marketing: true });
    setPrefs({ analytics: true, marketing: true });
    setShowBanner(false);
    setShowModal(false);
  };
  const rejectNonEssential = () => {
    setConsent({ analytics: false, marketing: false });
    setPrefs({ analytics: false, marketing: false });
    setShowBanner(false);
    setShowModal(false);
  };
  const savePrefs = () => {
    setConsent(prefs);
    setShowBanner(false);
    setShowModal(false);
  };

  return (
    <>
      {showBanner && (
        <div
          className="fixed bottom-3 left-3 right-3 md:left-auto md:right-5 md:bottom-5 z-[60] md:max-w-md"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
        >
          <div
            className="rounded-2xl shadow-2xl overflow-hidden"
            style={{ background: "var(--c-black)", border: "1px solid #2a2a28" }}
          >
            <div className="px-4 md:px-5 pt-4 pb-3 flex items-start gap-3">
              <div
                className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center"
                style={{ background: "#1a1a1a", border: "1px solid #2a2a28" }}
              >
                <i className="fa-solid fa-cookie-bite" style={{ color: "var(--c-gold)" }}></i>
              </div>
              <div className="text-white/85 text-sm leading-relaxed">
                <div className="font-display text-white text-base mb-1">We value your privacy</div>
                We use cookies to make Crown01 work, measure performance and personalise marketing. You can accept all, reject non-essential, or manage your preferences. See our{" "}
                <Link to="/cookies" className="underline" style={{ color: "var(--c-gold)" }} data-testid="cookie-banner-policy-link">Cookie Policy</Link>.
              </div>
            </div>
            <div className="px-4 md:px-5 pb-4 flex flex-wrap gap-2">
              <button
                onClick={acceptAll}
                className="btn !py-2 !px-4 !text-xs font-semibold flex-1 min-w-[120px]"
                style={{ background: "var(--c-red)", color: "#fff" }}
                data-testid="cookie-accept-all"
              >
                Accept All
              </button>
              <button
                onClick={rejectNonEssential}
                className="btn !py-2 !px-4 !text-xs font-semibold flex-1 min-w-[120px]"
                style={{ background: "#1a1a1a", color: "#fff", border: "1px solid #2a2a28" }}
                data-testid="cookie-reject-non-essential"
              >
                Reject Non-Essential
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="!py-2 !px-3 !text-xs font-semibold underline w-full text-center"
                style={{ color: "var(--c-gold)" }}
                data-testid="cookie-manage-preferences"
              >
                Manage Preferences
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <PreferencesModal
          prefs={prefs}
          setPrefs={setPrefs}
          onClose={() => setShowModal(false)}
          onSave={savePrefs}
          onAcceptAll={acceptAll}
          onRejectAll={rejectNonEssential}
        />
      )}
    </>
  );
}

function PreferencesModal({ prefs, setPrefs, onClose, onSave, onAcceptAll, onRejectAll }) {
  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm p-3 md:p-6"
      onClick={onClose}
      data-testid="cookie-preferences-modal"
    >
      <div
        className="w-full md:max-w-lg rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: "#fff" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Cookie preferences"
      >
        {/* Header */}
        <div className="px-5 md:px-6 py-4 flex items-center justify-between" style={{ background: "var(--c-black)" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#1a1a1a", border: "1px solid #2a2a28" }}>
              <i className="fa-solid fa-shield-halved" style={{ color: "var(--c-gold)" }}></i>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--c-gold)" }}>Privacy</div>
              <div className="font-display text-white text-lg leading-none mt-1">Cookie Preferences</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white"
            aria-label="Close"
            data-testid="cookie-preferences-close"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 md:px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          <p className="text-sm text-[color:var(--c-grey-700)] leading-relaxed">
            We use cookies to keep our site running, measure performance and personalise marketing. Necessary cookies are always on; you can switch the others on or off below. See our <Link to="/cookies" onClick={onClose} className="underline font-semibold" style={{ color: "var(--c-gold-deep)" }}>Cookie Policy</Link> for details.
          </p>

          <CategoryRow
            title="Necessary"
            description="Required for basket, secure checkout and remembering your session. Always on."
            checked
            disabled
            testid="cookie-toggle-necessary"
          />
          <CategoryRow
            title="Analytics"
            description="Anonymous usage statistics via Google Analytics 4. Helps us improve the site."
            checked={prefs.analytics}
            onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
            testid="cookie-toggle-analytics"
          />
          <CategoryRow
            title="Marketing"
            description="Meta (Facebook) and TikTok pixels — measure ad effectiveness and personalise offers."
            checked={prefs.marketing}
            onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
            testid="cookie-toggle-marketing"
          />
        </div>

        {/* Footer actions */}
        <div className="px-5 md:px-6 py-4 border-t flex flex-col sm:flex-row gap-2" style={{ borderColor: "var(--c-grey-100)", background: "var(--c-grey-50)" }}>
          <button
            onClick={onRejectAll}
            className="btn !py-2.5 !px-4 !text-xs font-semibold sm:flex-1"
            style={{ background: "#fff", color: "var(--c-black)", border: "1px solid var(--c-grey-100)" }}
            data-testid="cookie-modal-reject"
          >
            Reject Non-Essential
          </button>
          <button
            onClick={onSave}
            className="btn !py-2.5 !px-4 !text-xs font-semibold sm:flex-1"
            style={{ background: "var(--c-black)", color: "#fff" }}
            data-testid="cookie-modal-save"
          >
            Save Preferences
          </button>
          <button
            onClick={onAcceptAll}
            className="btn !py-2.5 !px-4 !text-xs font-semibold sm:flex-1"
            style={{ background: "var(--c-red)", color: "#fff" }}
            data-testid="cookie-modal-accept-all"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ title, description, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 p-4 rounded-xl" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-display text-base text-black">{title}</div>
          {disabled && (
            <span className="text-[10px] uppercase tracking-[0.16em] font-semibold px-1.5 py-0.5 rounded" style={{ background: "var(--c-gold)", color: "var(--c-black)" }}>Always on</span>
          )}
        </div>
        <p className="text-xs text-[color:var(--c-grey-700)] mt-1 leading-relaxed">{description}</p>
      </div>
      <ToggleSwitch
        checked={!!checked}
        disabled={disabled}
        onChange={onChange}
        testid={testid}
      />
    </div>
  );
}

function ToggleSwitch({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={`relative shrink-0 w-11 h-6 rounded-full transition ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
      style={{ background: checked ? "var(--c-gold-deep)" : "var(--c-grey-100)" }}
      data-testid={testid}
    >
      <span
        className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
        style={{ transform: checked ? "translateX(20px)" : "translateX(0)" }}
      />
    </button>
  );
}
